import { GraphLinksFormAtom } from "@features/builder/UpdateMetadata/GraphLinks/atoms"
import { GraphLink } from "@features/builder/UpdateMetadata/GraphLinks/types"
import { AppNode } from "@features/builder/graph/types"
import { linkEncode, linkParse } from "@components/NodeLink/linkHelpers"
import { useNodes } from "@xyflow/react"
import { useAtomCallback } from "jotai/utils"
import { useCallback, useEffect, useMemo } from "react"

const getLinks = (nodes: AppNode[]) => {
  const links = new Map<string, GraphLink>()

  for (const currNode of nodes) {
    if ("metaNode" in currNode.data && currNode.data.metaNode.Link) {
      const parsed = linkParse(currNode.data.metaNode.Link)
      if (!parsed) continue

      const { org, div, prod, family, version } = parsed

      const graphFamily = linkEncode(org, div, prod, family)
      if (!graphFamily) continue

      if (links.has(graphFamily)) {
        const existing = links.get(graphFamily)!
        if (!existing.usedVersion.includes(version)) {
          existing.usedVersion.push(version)
        }
        existing.nodeIds.push(currNode.id)
      } else {
        links.set(graphFamily, {
          nodeIds: [currNode.id],
          linkedGraphFamily: graphFamily,
          usedVersion: [version],
        })
      }
    }
  }

  return links
}

export const useInitGraphLinks = () => {
  const nodes = useNodes<AppNode>()

  // Stable serialized key so freshLinks only changes when node link data actually changes
  const linkDataKey = nodes
    .map((n) => ("metaNode" in n.data ? `${n.id}:${n.data.metaNode.Link}` : ""))
    .join("|")

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const freshLinks = useMemo(() => getLinks(nodes), [linkDataKey])

  const syncLinks = useAtomCallback(
    useCallback(
      (get, set) => {
        const existing = new Map(
          get(GraphLinksFormAtom).map((link) => [link.linkedGraphFamily, link]),
        )
        const next: GraphLink[] = []

        for (const [family, link] of freshLinks) {
          const prev = existing.get(family)
          next.push({ ...link, selectedVersion: prev?.selectedVersion })
        }

        set(GraphLinksFormAtom, next)
      },
      [freshLinks],
    ),
  )

  useEffect(() => {
    syncLinks()
  }, [syncLinks])
}
